import axios from "axios";
import { triggerUpsell } from "../utils/upsellBus";

const api = axios.create({
    baseURL: '/api',
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
    }

    const lang = localStorage.getItem("gredus.language") || "az";
    config.headers["Accept-Language"] = lang;

    const method = (config.method || "").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
        config.headers["Idempotency-Key"] = crypto.randomUUID();
    }

    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
};

const noRefreshEndpoints = [
    "/auth/login",
    "/auth/register",
    "/auth/register/confirm",
    "/auth/forgot-password",
    "/auth/refresh-token",
    "/auth/logout",
];

// When we're about to redirect to /login (auth fully expired), return a never-resolving
// promise instead of rejecting. This swallows the original error so components that do
// `toast.error(err.response.data.message)` don't flash the raw "JWT expired ..." text
// during the half-second the browser takes to navigate.
const swallowedDuringRedirect = () => new Promise(() => {});

api.interceptors.response.use(
    (res) => res,
    async (err) => {
        const status = err?.response?.status;
        const originalRequest = err?.config;
        const url = originalRequest?.url || "";

        // Subscription/quota limit hit (per-action). Don't dead-end the user: open the upsell modal
        // with packs to buy. We still reject so the calling form clears its loading state — the form's
        // filled-in data is left untouched, so the user keeps everything they entered.
        if (err?.response?.data?.code === "SUBSCRIPTION_LIMIT_REACHED") {
            err.isSubscriptionLimit = true;
            triggerUpsell({ message: err.response.data.message });
            return Promise.reject(err);
        }

        // 402 Payment Required = backend hard-gate filter; tenant's subscription has expired.
        // Force the user to the subscription page (unless they're already on the payment flow).
        if (status === 402) {
            const onPaymentRoute =
                window.location.pathname === "/settings/subscription" ||
                window.location.pathname.startsWith("/payment/");
            if (!onPaymentRoute) {
                window.location.href = "/settings/subscription";
            }
            return Promise.reject(err);
        }

        const isNoRefreshEndpoint = noRefreshEndpoints.some((ep) => url.includes(ep));

        if (status !== 401 || isNoRefreshEndpoint) {
            if (status === 401 && url.includes("/auth/refresh-token")) {
                localStorage.clear();
                window.location.href = "/login";
                return swallowedDuringRedirect();
            }
            return Promise.reject(err);
        }

        if (originalRequest._retry) {
            localStorage.clear();
            window.location.href = "/login";
            return Promise.reject(err);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((newToken) => {
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                return api(originalRequest);
            }).catch((e) => Promise.reject(e));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const refreshRes = await api.post("/auth/refresh-token");
            const newAccessToken = refreshRes.data.accessToken;

            localStorage.setItem("token", newAccessToken);
            api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;

            processQueue(null, newAccessToken);

            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            localStorage.clear();
            window.location.href = "/login";
            // Don't surface "JWT expired ..." to the caller — page is navigating away.
            return swallowedDuringRedirect();
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
